import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getSpareParts, deleteSparePart } from '../../redux/features/sparePartSlice';
import { Pencil, Trash2, Loader2, ArrowLeft, AlertTriangle, Search, Filter, Eye, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import SparePartForm from './SparePartForm';

const ITEMS_PER_PAGE = 10;

const formatPrice = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  return new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(num);
};

const sparePartStockValue = (part) =>
  (Number(part?.quantity) || 0) * (Number(part?.price) || 0);

const isLowStock = (part) =>
  Number(part?.minQuantity) > 0 && Number(part?.quantity) <= Number(part?.minQuantity);

const SparePartList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { items: spareParts, loading } = useSelector((state) => {
    console.log('Redux State:', state);
    return state.spareParts || { items: [], loading: false };
  });
  const [showForm, setShowForm] = useState(false);
  const [selectedSparePart, setSelectedSparePart] = useState(null);
  const [selectedSparePartDetails, setSelectedSparePartDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [minQtyWarningFilter, setMinQtyWarningFilter] = useState('all');
  const [priceSort, setPriceSort] = useState('none');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(getSpareParts());
  }, [dispatch]);

  const handleEdit = (sparePart) => {
    setSelectedSparePart(sparePart);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Da li ste sigurni da želite da obrišete ovaj rezervni deo?')) {
      try {
        await dispatch(deleteSparePart(id)).unwrap();
        toast.success('Rezervni deo je uspešno obrisan');
      } catch (error) {
        toast.error(error.message || 'Greška pri brisanju rezervnog dela');
      }
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setSelectedSparePart(null);
  };

  const categories = useMemo(() => {
    const set = new Set();
    spareParts.forEach((part) => {
      if (part?.category) set.add(part.category);
    });
    return Array.from(set).sort((a, b) => String(a).localeCompare(String(b), 'sr', { sensitivity: 'base' }));
  }, [spareParts]);

  const filteredAndSortedParts = useMemo(() => {
    let list = Array.isArray(spareParts) ? [...spareParts] : [];

    const searchLower = searchTerm.trim().toLowerCase();
    if (searchLower) {
      list = list.filter((part) => {
        const name = String(part?.name || '').toLowerCase();
        const code = String(part?.code || '').toLowerCase();
        const category = String(part?.category || '').toLowerCase();
        return name.includes(searchLower) || code.includes(searchLower) || category.includes(searchLower);
      });
    }

    if (categoryFilter !== 'all') {
      list = list.filter((part) => String(part?.category || '') === categoryFilter);
    }

    if (minQtyWarningFilter === 'only-warning') {
      list = list.filter((part) => Number(part?.quantity || 0) < Number(part?.minQuantity || 0));
    } else if (minQtyWarningFilter === 'only-min') {
      list = list.filter((part) => Number(part?.quantity || 0) === Number(part?.minQuantity || 0));
    }

    if (priceSort === 'asc') {
      list.sort((a, b) => Number(a?.price ?? Number.POSITIVE_INFINITY) - Number(b?.price ?? Number.POSITIVE_INFINITY));
    } else if (priceSort === 'desc') {
      list.sort((a, b) => Number(b?.price ?? Number.NEGATIVE_INFINITY) - Number(a?.price ?? Number.NEGATIVE_INFINITY));
    } else {
      list.sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'sr', { sensitivity: 'base' }));
    }

    return list;
  }, [spareParts, searchTerm, categoryFilter, minQtyWarningFilter, priceSort]);

  const totalPages = Math.max(1, Math.ceil(filteredAndSortedParts.length / ITEMS_PER_PAGE));

  const paginatedParts = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredAndSortedParts.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredAndSortedParts, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter, minQtyWarningFilter, priceSort]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (showForm) {
    return (
      <div className="bg-gray-800/90 rounded-lg border border-gray-700/80 p-3">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={handleCloseForm}
            className="flex items-center text-sm text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t('spareParts.backToList')}
          </button>
          <h2 className="text-lg sm:text-sm font-semibold text-white">
            {selectedSparePart ? t('spareParts.editSparePart') : t('spareParts.addNew')}
          </h2>
        </div>
        <SparePartForm
          isEdit={!!selectedSparePart}
          sparePart={selectedSparePart}
          onClose={handleCloseForm}
        />
      </div>
    );
  }

  return (
    <div className="bg-gray-800/90 rounded-lg border border-gray-700/80 p-3">
      <div className="grid grid-cols-2 lg:grid-cols-12 gap-2 mb-3 items-center">
        <h2 className="text-base sm:text-lg font-semibold text-white">{t('spareParts.title')}</h2>
        <div className="col-span-1 lg:col-span-8 flex items-center gap-1.5 bg-gray-700 rounded-md px-2.5 py-1.5">
          <Search className="w-3.5 h-3.5 text-gray-300" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Pretraga rezervnih delova..."
            className="w-full bg-transparent text-xs sm:text-sm text-white placeholder:text-gray-400 focus:outline-none"
          />
        </div>
        <button
          type="button"
          onClick={() => setShowFilters((prev) => !prev)}
          className="col-span-1 lg:col-span-3 flex items-center justify-center px-3 py-1.5 bg-gray-700 text-white rounded-md hover:bg-gray-600 transition-colors text-xs sm:text-sm"
        >
          <Filter className="w-3.5 h-3.5 mr-1.5" />
          Filter
        </button>
      </div>

      {showFilters && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 p-3 bg-gray-700 rounded-md">
          <div>
            <label className="block text-xs text-gray-300 mb-1">Kategorija</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full h-9 rounded-md bg-gray-800 border border-gray-600 text-white text-sm px-2"
            >
              <option value="all">Sve kategorije</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-300 mb-1">Stanje zaliha</label>
            <select
              value={minQtyWarningFilter}
              onChange={(e) => setMinQtyWarningFilter(e.target.value)}
              className="w-full h-9 rounded-md bg-gray-800 border border-gray-600 text-white text-sm px-2"
            >
              <option value="all">Prikaži sve</option>
              <option value="only-warning">Ispod minimalne zalihe</option>
              <option value="only-min">Tačno na minimalnoj zalihi</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-300 mb-1">Sortiranje po ceni</label>
            <select
              value={priceSort}
              onChange={(e) => setPriceSort(e.target.value)}
              className="w-full h-9 rounded-md bg-gray-800 border border-gray-600 text-white text-sm px-2"
            >
              <option value="none">Abecedno (A-Z)</option>
              <option value="asc">Najjeftinije - najskuplje</option>
              <option value="desc">Najskuplje - najjeftinije</option>
            </select>
          </div>
        </div>
      )}

      <div className="hidden md:block overflow-x-auto rounded-lg">
        <table className="min-w-full bg-gray-800 shadow-sm">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-left">{t('spareParts.name')}</th>
              <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-left">{t('spareParts.code')}</th>
              <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-left">{t('spareParts.category')}</th>
              <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-left">Dobavljač</th>
              <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-right">Nabavna/kom</th>
              <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-right">Prodajna/kom</th>
              <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-right">Ukupna vrednost</th>
              <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-right">{t('spareParts.tax')}</th>
              <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-right">{t('spareParts.quantity')}</th>
              <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-right">{t('spareParts.minQuantity')}</th>
              <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-center">{t('spareParts.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {paginatedParts.map((sparePart) => (
              <tr key={sparePart._id} className="hover:bg-gray-700">
                <td className="px-2.5 py-1.5 text-xs">
                  <div>
                    <div className="font-medium text-white">{sparePart.name}</div>
                    {sparePart.description && (
                      <div className="text-xs text-gray-400">{sparePart.description}</div>
                    )}
                  </div>
                </td>
                <td className="px-2.5 py-1.5 text-xs text-gray-300">{sparePart.code}</td>
                <td className="px-2.5 py-1.5 text-xs text-gray-300">{sparePart.category}</td>
                <td className="px-2.5 py-1.5 text-xs text-gray-400">{sparePart.supplierName || sparePart.arrivalSource || '-'}</td>
                <td className="px-2.5 py-1.5 text-xs text-gray-300 text-right">{formatPrice(sparePart.purchasePrice)}</td>
                <td className="px-2.5 py-1.5 text-xs text-gray-300 text-right">{formatPrice(sparePart.price)}</td>
                <td className="px-2.5 py-1.5 text-xs text-white text-right font-medium">{formatPrice(sparePartStockValue(sparePart))}</td>
                <td className="px-2.5 py-1.5 text-xs text-gray-300 text-right">{sparePart.tax}</td>
                <td className="px-2.5 py-1.5 text-xs text-right">
                  <div className="flex items-center justify-end space-x-1">
                    <span className={isLowStock(sparePart) ? 'text-red-400 font-medium' : 'text-gray-300'}>
                      {sparePart.quantity}
                    </span>
                    {isLowStock(sparePart) && (
                      <div className="group relative">
                        <AlertTriangle className="w-4 h-4 text-yellow-500" />
                        <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block">
                          <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                            {t('spareParts.quantityBelowMin')} ({sparePart.minQuantity})
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-2.5 py-1.5 text-xs text-gray-300 text-right">{sparePart.minQuantity}</td>
                <td className="px-2.5 py-1.5 text-xs text-right">
                  <div className="flex justify-center space-x-2">
                    <button
                      onClick={() => handleEdit(sparePart)}
                      className="p-1 text-blue-400 hover:text-blue-300"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(sparePart._id)}
                      className="p-1 text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-2">
        {paginatedParts.map((sparePart) => (
          <div key={sparePart._id} className="bg-gray-700 rounded-md p-3">
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-white truncate">{sparePart.name || '-'}</div>
                <div className="text-xs text-gray-300 whitespace-nowrap">
                  {formatPrice(sparePartStockValue(sparePart))}
                </div>
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs text-gray-300 inline-flex items-center gap-1">
                  Količina: {Number(sparePart.quantity ?? 0)}
                  {isLowStock(sparePart) && (
                    <AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0" />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedSparePartDetails(sparePart)}
                  className="px-2 py-1 text-[11px] bg-gray-800 text-white rounded-md flex items-center"
                >
                  <Eye className="w-3 h-3 mr-1" />
                  Vidi više
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="text-xs text-gray-300">
          Prikazano {paginatedParts.length} od {filteredAndSortedParts.length}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-2 py-1 bg-gray-700 text-white rounded disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-white">{currentPage} / {totalPages}</span>
          <button
            type="button"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
            className="px-2 py-1 bg-gray-700 text-white rounded disabled:opacity-50"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {selectedSparePartDetails && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-gray-800 rounded-lg border border-gray-700 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold">Detalji rezervnog dela</h3>
              <button
                type="button"
                onClick={() => setSelectedSparePartDetails(null)}
                className="text-gray-300 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-1 text-sm text-gray-200">
              <div><span className="text-gray-400">Naziv:</span> {selectedSparePartDetails.name || '-'}</div>
              <div><span className="text-gray-400">Šifra:</span> {selectedSparePartDetails.code || '-'}</div>
              <div><span className="text-gray-400">Kategorija:</span> {selectedSparePartDetails.category || '-'}</div>
              <div><span className="text-gray-400">Dobavljač:</span> {selectedSparePartDetails.supplierName || selectedSparePartDetails.arrivalSource || '-'}</div>
              <div><span className="text-gray-400">Nabavna/kom:</span> {formatPrice(selectedSparePartDetails.purchasePrice)}</div>
              <div><span className="text-gray-400">Prodajna/kom:</span> {formatPrice(selectedSparePartDetails.price)}</div>
              <div><span className="text-gray-400">Ukupna vrednost:</span> {formatPrice(sparePartStockValue(selectedSparePartDetails))}</div>
              <div><span className="text-gray-400">Porez:</span> {selectedSparePartDetails.tax ?? '-'}</div>
              <div><span className="text-gray-400">Količina:</span> {selectedSparePartDetails.quantity ?? 0}</div>
              <div><span className="text-gray-400">Minimalna količina:</span> {selectedSparePartDetails.minQuantity ?? 0}</div>
              {selectedSparePartDetails.description && (
                <div><span className="text-gray-400">Opis:</span> {selectedSparePartDetails.description}</div>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  handleEdit(selectedSparePartDetails);
                  setSelectedSparePartDetails(null);
                }}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm"
              >
                Izmeni
              </button>
              <button
                type="button"
                onClick={() => {
                  handleDelete(selectedSparePartDetails._id);
                  setSelectedSparePartDetails(null);
                }}
                className="px-3 py-1.5 bg-red-600 text-white rounded-md text-sm"
              >
                Obriši
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SparePartList; 